import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import SqrtInput from "../../../../components/inputs/SqrtInput";

// --- generator ---

// sensowny zakres długości odcinków (cm), żeby liczby nie były za duże
const EDGES = [2, 3, 4, 5, 6, 7, 8, 9, 10];

// trójkąty prostokątne z całkowitymi bokami
const RIGHT_TRIANGLE_TRIPLES = [
  { a: 3, b: 4, c: 5 },
  { a: 6, b: 8, c: 10 },
];

function randomFrom(array) {
  return array[Math.floor(Math.random() * array.length)];
}

function randomEdge() {
  return randomFrom(EDGES);
}

function randomEdgeDifferentFrom(x) {
  let v;
  do {
    v = randomEdge();
  } while (v === x);
  return v;
}

// wysokości z pierwiastkiem typu k√m
const ROOT_HEIGHT_BASES = [2, 3, 5, 6, 7, 10, 11, 13, 15];
const ROOT_HEIGHT_MULTIPLIERS = [2, 3, 4, 5, 6];

function randomRootHeight() {
  const base = randomFrom(ROOT_HEIGHT_BASES);
  const coeff = randomFrom(ROOT_HEIGHT_MULTIPLIERS);
  const value = coeff * Math.sqrt(base);

  return {
    value,
    isRoot: true,
    rootBase: base,
    rootCoeff: coeff,
  };
}

// allowRoot = true -> czasem wysokość postaci k√m
// allowRoot = false -> zawsze zwykła liczba całkowita
function randomHeight(allowRoot = true) {
  if (!allowRoot) {
    const v = randomEdge();
    return { value: v, isRoot: false, rootBase: null, rootCoeff: 1 };
  }

  const useRoot = Math.random() < 0.5;
  if (!useRoot) {
    const v = randomEdge();
    return { value: v, isRoot: false, rootBase: null, rootCoeff: 1 };
  }

  return randomRootHeight();
}

function makeHeight(allowRoot = true) {
  const hObj = randomHeight(allowRoot);
  const h = hObj.value;

  let hDisplay;
  if (hObj.isRoot) {
    if (hObj.rootCoeff === 1) {
      hDisplay = `\\sqrt{${hObj.rootBase}}`;
    } else {
      hDisplay = `${hObj.rootCoeff}\\sqrt{${hObj.rootBase}}`;
    }
  } else {
    hDisplay = String(h);
  }

  return {
    h,
    hDisplay,
    hIsRoot: !!hObj.isRoot,
    hRootBase: hObj.isRoot ? hObj.rootBase : null,
    hCoeff: hObj.isRoot ? hObj.rootCoeff : 1,
  };
}

// --- ogólny generator długości z możliwością pierwiastka (będzie używany w bokach podstaw) ---

// korzystamy z tych samych baz i współczynników co dla wysokości
const ROOT_LENGTH_BASES = ROOT_HEIGHT_BASES;
const ROOT_LENGTH_MULTIPLIERS = ROOT_HEIGHT_MULTIPLIERS;

function randomRootLength() {
  const base = randomFrom(ROOT_LENGTH_BASES);
  const coeff = randomFrom(ROOT_LENGTH_MULTIPLIERS);
  const value = coeff * Math.sqrt(base);

  return {
    value,
    isRoot: true,
    rootBase: base,
    rootCoeff: coeff,
  };
}

// allowRoot = true -> czasem długość postaci k√m
// allowRoot = false -> zawsze zwykła liczba całkowita
function randomLength(allowRoot = true) {
  if (!allowRoot) {
    const v = randomEdge();
    return { value: v, isRoot: false, rootBase: null, rootCoeff: 1 };
  }

  const useRoot = Math.random() < 0.5;
  if (!useRoot) {
    const v = randomEdge();
    return { value: v, isRoot: false, rootBase: null, rootCoeff: 1 };
  }

  return randomRootLength();
}

function makeLength(allowRoot = true) {
  const lenObj = randomLength(allowRoot);
  const value = lenObj.value;

  let display;
  if (lenObj.isRoot) {
    if (lenObj.rootCoeff === 1) {
      display = `\\sqrt{${lenObj.rootBase}}`;
    } else {
      display = `${lenObj.rootCoeff}\\sqrt{${lenObj.rootBase}}`;
    }
  } else {
    display = String(value);
  }

  return {
    value,
    display,
    isRoot: !!lenObj.isRoot,
    rootBase: lenObj.isRoot ? lenObj.rootBase : null,
    coeff: lenObj.isRoot ? lenObj.rootCoeff : 1,
  };
}


function isValidTriangle(a, b, c) {
  return a + b > c && a + c > b && b + c > a;
}



// --- warianty trójkątów ---

// --- warianty trójkątów ---

function generateTriangleScalene() {
  let a, b, c;
  do {
    a = randomEdge();
    b = randomEdge();
    c = randomEdge();
  } while (!isValidTriangle(a, b, c));

  const { h, hDisplay, hIsRoot, hRootBase, hCoeff } = makeHeight(true);
  const perimeter = a + b + c;

  return {
    variant: "triangle_scalene",
    nSides: 3,
    a,
    b,
    c,
    h,
    hDisplay,
    hIsRoot,
    hRootBase,
    hCoeff,
    perimeter,
  };
}

function generateTriangleRight() {
  const triple = randomFrom(RIGHT_TRIANGLE_TRIPLES);
  const a = triple.a;
  const b = triple.b;
  const c = triple.c;

  const { h, hDisplay, hIsRoot, hRootBase, hCoeff } = makeHeight(true);

  // co podajemy w treści zadania:
  // "legs"  -> dwie przyprostokątne
  // "a_c"   -> przyprostokątna a i przeciwprostokątna c
  // "b_c"   -> przyprostokątna b i przeciwprostokątna c
  const givenType = randomFrom(["legs", "a_c", "b_c"]);

  const perimeter = a + b + c;

  return {
    variant: "triangle_right",
    nSides: 3,
    a,
    b,
    c,
    h,
    hDisplay,
    hIsRoot,
    hRootBase,
    hCoeff,
    perimeter,
    givenType,
  };
}

function generateTriangleEquilateral() {
  // bok podstawy: dopuszczamy pierwiastki
  const side = makeLength(true);
  const a = side.value;
  const aDisplay = side.display;

  const hData = makeHeight(true);
  const { h, hDisplay, hIsRoot, hRootBase, hCoeff } = hData;

  const perimeter = 3 * a;
  const lateralArea = perimeter * h;

  return {
    variant: "triangle_equilateral",
    baseType: "triangle_equilateral",
    baseName: "trójkąta równobocznego",
    a,
    aDisplay,
    perimeter,
    h,
    hDisplay,
    hIsRoot,
    hRootBase,
    hCoeff,
    lateralArea,
  };
}



// --- czworokąty (w tym sześcian) ---

function generateSquareOrCube() {
  const isCube = Math.random() < 0.5;

  if (isCube) {
    // SZEŚCIAN – krawędź może mieć pierwiastek, wysokość równa krawędzi
    const side = makeLength(true);     // dokładnie tak jak w graniastosłupie prawidłowym
    const a = side.value;
    const aDisplay = side.display;

    // w sześcianie wysokość = krawędź, także w zapisie z pierwiastkiem
    const h = a;
    const hDisplay = aDisplay;
    const hIsRoot = side.isRoot;
    const hRootBase = side.isRoot ? side.rootBase : null;
    const hCoeff = side.isRoot ? side.coeff : 1;

    const perimeter = 4 * a;
    const lateralArea = perimeter * h;

    return {
      variant: "square",
      baseType: "square",
      isCube: true,
      baseName: "kwadratu",
      a,
      aDisplay,
      perimeter,
      h,
      hDisplay,
      hIsRoot,
      hRootBase,
      hCoeff,
      lateralArea,
    };
  }

  // graniastosłup prawidłowy czworokątny – bok może mieć pierwiastek
  const side = makeLength(true);
  const a = side.value;
  const aDisplay = side.display;

  const hData = makeHeight(true);
  const { h, hDisplay, hIsRoot, hRootBase, hCoeff } = hData;

  const perimeter = 4 * a;
  const lateralArea = perimeter * h;

  return {
    variant: "square",
    baseType: "square",
    isCube: false,
    baseName: "kwadratu",
    a,
    aDisplay,
    perimeter,
    h,
    hDisplay,
    hIsRoot,
    hRootBase,
    hCoeff,
    lateralArea,
  };
}

function generateRectangle() {
  // Bok a – dopuszczamy pierwiastek
  const sideA = makeLength(true);
  const a = sideA.value;
  const aDisplay = sideA.display;

  // Bok b – też dopuszczamy pierwiastek (nie musi być równy a)
  const sideB = makeLength(true);
  let b = sideB.value;        // ← tu zmieniamy na let
  let bDisplay = sideB.display; // ← tu też let

  // Jeśli przez przypadek wyszedł "kwadrat", losujemy b od nowa
  if (a === b) {
    const sideB2 = makeLength(true);
    // tu już nie ma dramatu, jak wyjdzie znowu równe, to trudno – szansa jest mała
    const b2 = sideB2.value;
    const bDisplay2 = sideB2.display;
    if (b2 !== a) {
      // tylko jeśli realnie zmieniliśmy długość, podmieniamy
      b = b2;
      bDisplay = bDisplay2;
    }
  }

  const { h, hDisplay, hIsRoot, hRootBase, hCoeff } = makeHeight(true);
  const perimeter = 2 * (a + b);
  const lateralArea = perimeter * h;

    return {
    variant: "rectangle",
    nSides: 4,
    a,
    b,
    aDisplay,
    bDisplay,
    h,
    hDisplay,
    hIsRoot,
    hRootBase,
    hCoeff,
    perimeter,
    lateralArea,
  };
}


function generateRhombus() {
  const side = makeLength(true);
  const a = side.value;
  const aDisplay = side.display;

  const hData = makeHeight(true);
  const { h, hDisplay, hIsRoot, hRootBase, hCoeff } = hData;

  const perimeter = 4 * a;
  const lateralArea = perimeter * h;

  // symboliczny zapis obwodu (z pierwiastkiem, jeśli a ma pierwiastek)
  let perimeterDisplay;
  if (side.isRoot) {
    const totalCoeff = 4 * side.coeff; // np. 4 * 3 = 12
    if (totalCoeff === 1) {
      perimeterDisplay = `\\sqrt{${side.rootBase}}`;
    } else {
      perimeterDisplay = `${totalCoeff}\\sqrt{${side.rootBase}}`;
    }
  } else {
    // gdy bok jest zwykłą liczbą
    perimeterDisplay = String(perimeter);
  }

  return {
    variant: "rhombus",
    baseType: "rhombus",
    baseName: "rombu",
    a,
    aDisplay,
    perimeter,
    perimeterDisplay,
    h,
    hDisplay,
    hIsRoot,
    hRootBase,
    hCoeff,
    lateralArea,
  };
}




function generateParallelogram() {
  // Bok a – dopuszczamy pierwiastek
  const sideA = makeLength(true);
  const a = sideA.value;
  const aDisplay = sideA.display;

  // Bok b – też dopuszczamy pierwiastek, ale staramy się uniknąć rombu
  let sideB;
  do {
    sideB = makeLength(true);
  } while (sideB.value === a && sideB.display === aDisplay);

  const b = sideB.value;
  const bDisplay = sideB.display;

  // Wysokość – jak w innych typach, może mieć pierwiastek
  const { h, hDisplay, hIsRoot, hRootBase, hCoeff } = makeHeight(true);

  const perimeter = 2 * (a + b);
  const lateralArea = perimeter * h;

  return {
    variant: "parallelogram",
    nSides: 4,
    a,
    b,
    aDisplay,
    bDisplay,
    h,
    hDisplay,
    hIsRoot,
    hRootBase,
    hCoeff,
    perimeter,
    lateralArea,
  };
}


// --- wielokąty foremne 5–7 ---

const REGULAR_POLYGON_NAME = {
  5: "pięciokąt foremny",
  6: "sześciokąt foremny",
  7: "siedmiokąt foremny",
};


function generateRegularPolygonPrism(nSides) {
  const side = makeLength(true);
  const a = side.value;
  const aDisplay = side.display;

  const hData = makeHeight(true);
  const { h, hDisplay, hIsRoot, hRootBase, hCoeff } = hData;

  const perimeter = nSides * a;
  const lateralArea = perimeter * h;

  return {
    variant: `regular_${nSides}`,
    baseType: `regular_${nSides}`,
    baseName: REGULAR_POLYGON_NAME[nSides],
    nSides,
    a,
    aDisplay,
    perimeter,
    h,
    hDisplay,
    hIsRoot,
    hRootBase,
    hCoeff,
    lateralArea,
  };
}




// --- główny generator ---


// --- główny generator ---

export function generatePrismLateralSurfaceTask() {
  // losujemy liczbę boków podstawy 3–7
  const nSides = randomFrom([3, 4, 5, 6, 7]);
  let core;

  if (nSides === 3) {
    // wybór typu trójkąta: dowolny / prostokątny / równoboczny
    const triangleVariant = randomFrom(["scalene", "right", "equilateral"]);

    if (triangleVariant === "scalene") {
      core = generateTriangleScalene();
    } else if (triangleVariant === "right") {
      core = generateTriangleRight();
    } else {
      core = generateTriangleEquilateral();
    }
  } else if (nSides === 4) {
    // wybór typu czworokąta w podstawie
    const quadVariant = randomFrom([
      "square_or_cube",
      "rectangle",
      "rhombus",
      "parallelogram",
    ]);

    if (quadVariant === "square_or_cube") {
      core = generateSquareOrCube();
    } else if (quadVariant === "rectangle") {
      core = generateRectangle();
    } else if (quadVariant === "rhombus") {
      core = generateRhombus();
    } else {
      core = generateParallelogram();
    }
  } else if (nSides === 5 || nSides === 6 || nSides === 7) {
    // dla 5–7 kątów: graniastosłup prawidłowy
    core = generateRegularPolygonPrism(nSides);
  }

  const { perimeter, h } = core;
  const lateralArea = perimeter * h; // P_b = O_p * h

  const data = {
    ...core,
    perimeter,
    lateralArea,
  };

  return {
    type: "LateralSurfaceTask",
    ...data,
    data,
  };
}

// --- eksport dla systemu quizowego ---

export function generateTask() {
  return generatePrismLateralSurfaceTask();
}



function unwrapTask(taskOrWrapper) {
  if (!taskOrWrapper) return taskOrWrapper;
  return taskOrWrapper.data ? taskOrWrapper.data : taskOrWrapper;
}

// --- parser wyrażeń z pierwiastkiem (skopiowany z PrismDiagonalTask) ---

function evaluateExpression(expr) {
  if (expr == null) return NaN;

  const cleaned = String(expr)
    .replace(/\s/g, "")
    .replace(/\u200b/g, "")
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
    .replace(/,/g, "."); // przecinek -> kropka

  // Normalizacja zapisów pierwiastka
  const normalized = cleaned
    // \sqrt{...}, sqrt{...}, sqrt(...)
    .replace(/\\\\sqrt\{([^}]+)\}/g, "√$1")
    .replace(/\\sqrt\{([^}]+)\}/g, "√$1")
    .replace(/sqrt\{([^}]+)\}/g, "√$1")
    .replace(/sqrt\(([^)]+)\)/g, "√$1")
    // √{...}, √(...)
    .replace(/√\{([^}]+)\}/g, "√$1")
    .replace(/√\(([^)]+)\)/g, "√$1");

  try {
    const jsExpr = normalized
      // 2√3, 5√10 itd.
      .replace(/(\d*)√(\d+(\.\d+)?)/g, (_, a, b) => `${a || "1"}*Math.sqrt(${b})`)
      // samo √289
      .replace(/√(\d+(\.\d+)?)/g, (_, b) => `Math.sqrt(${b})`);

    const val = Function(`"use strict"; return (${jsExpr});`)();
    if (typeof val !== "number" || !isFinite(val)) return NaN;
    return val;
  } catch {
    return NaN;
  }
}


// --- treść zadania ---

export function renderTask(taskOrWrapper) {
  const task = unwrapTask(taskOrWrapper);
  const { variant, h, hDisplay } = task;
  const heightLatex = hDisplay || h;

  if (variant === "triangle_scalene") {
    const { a, b, c } = task;

    return (
      <p>
        Dany jest graniastosłup prosty, którego podstawą jest trójkąt o bokach{" "}
        <InlineMath math={`a = ${a}\\,\\text{cm}`} />,{" "}
        <InlineMath math={`b = ${b}\\,\\text{cm}`} /> i{" "}
        <InlineMath math={`c = ${c}\\,\\text{cm}`} />. Wysokość
        graniastosłupa wynosi{" "}
        <InlineMath math={`h = ${heightLatex}\\,\\text{cm}`} />. Oblicz pole
        powierzchni bocznej tego graniastosłupa.
      </p>
    );
  }



  if (variant === "triangle_right") {
    const { a, b, c, h, givenType } = task;

    if (givenType === "legs") {
      return (
        <p>
          Dany jest graniastosłup prosty, którego podstawą jest trójkąt
          prostokątny o przyprostokątnych{" "}
          <InlineMath math={`a = ${a}\\,\\text{cm}`} /> i{" "}
          <InlineMath math={`b = ${b}\\,\\text{cm}`} />. Wysokość graniastosłupa
          wynosi <InlineMath math={`h = ${heightLatex}\\,\\text{cm}`} />. Oblicz pole
          powierzchni bocznej tego graniastosłupa.
        </p>
      );
    }

    if (givenType === "a_c") {
      return (
        <p>
          Dany jest graniastosłup prosty, którego podstawą jest trójkąt
          prostokątny o przyprostokątnej{" "}
          <InlineMath math={`a = ${a}\\,\\text{cm}`} /> i przeciwprostokątnej{" "}
          <InlineMath math={`c = ${c}\\,\\text{cm}`} />. Wysokość graniastosłupa
          wynosi <InlineMath math={`h = ${heightLatex}\\,\\text{cm}`} />. Oblicz pole
          powierzchni bocznej tego graniastosłupa.
        </p>
      );
    }

    // givenType === "b_c"
    return (
      <p>
        Dany jest graniastosłup prosty, którego podstawą jest trójkąt
        prostokątny o przyprostokątnej{" "}
        <InlineMath math={`b = ${b}\\,\\text{cm}`} /> i przeciwprostokątnej{" "}
        <InlineMath math={`c = ${c}\\,\\text{cm}`} />. Wysokość graniastosłupa
        wynosi <InlineMath math={`h = ${heightLatex}\\,\\text{cm}`} />. Oblicz pole
        powierzchni bocznej tego graniastosłupa.
      </p>
    );
  }

if (variant === "triangle_equilateral") {
  const { aDisplay, hDisplay } = task;
  return (
    <p>
      Dany jest graniastosłup prosty, którego podstawą jest trójkąt równoboczny
      o boku <InlineMath math={`a = ${aDisplay}\\,\\text{cm}`} />. Wysokość
      graniastosłupa wynosi{" "}
      <InlineMath math={`h = ${hDisplay}\\,\\text{cm}`} />. Oblicz pole
      powierzchni bocznej tego graniastosłupa.
    </p>
  );
}




if (variant === "square") {
  const { aDisplay, hDisplay, isCube } = task;

  if (isCube) {
    return (
      <p>
        Dany jest sześcian o krawędzi{" "}
        <InlineMath math={`a = ${aDisplay}\\,\\text{cm}`} />. Oblicz pole
        powierzchni bocznej tego sześcianu.
      </p>
    );
  }

  return (
    <p>
      Dany jest graniastosłup prawidłowy czworokątny o boku{" "}
      <InlineMath math={`a = ${aDisplay}\\,\\text{cm}`} /> i wysokości{" "}
      <InlineMath math={`h = ${hDisplay}\\,\\text{cm}`} />. Oblicz pole
      powierzchni bocznej tego graniastosłupa.
    </p>
  );
}





  if (variant === "rectangle") {
  const { a, b, aDisplay, bDisplay } = task;

  const aText = aDisplay != null ? aDisplay : a;
  const bText = bDisplay != null ? bDisplay : b;

  return (
    <p>
      Dany jest graniastosłup prosty, którego podstawą jest prostokąt o bokach{" "}
      <InlineMath math={`a = ${aText}\\,\\text{cm}`} /> i{" "}
      <InlineMath math={`b = ${bText}\\,\\text{cm}`} />. Wysokość
      graniastosłupa wynosi{" "}
      <InlineMath math={`h = ${heightLatex}\\,\\text{cm}`} />. Oblicz pole
      powierzchni bocznej tego graniastosłupa.
    </p>
  );
}




if (variant === "rhombus") {
  const { aDisplay, hDisplay } = task;
  return (
    <p>
      Dany jest graniastosłup prosty, którego podstawą jest romb o boku{" "}
      <InlineMath math={`a = ${aDisplay}\\,\\text{cm}`} />. Wysokość
      graniastosłupa wynosi{" "}
      <InlineMath math={`h = ${hDisplay}\\,\\text{cm}`} />. Oblicz pole
      powierzchni bocznej tego graniastosłupa.
    </p>
  );
}





  if (variant === "parallelogram") {
  const { a, b, aDisplay, bDisplay } = task;

  const aText = aDisplay != null ? aDisplay : a;
  const bText = bDisplay != null ? bDisplay : b;

  return (
    <p>
      Dany jest graniastosłup prosty, którego podstawą jest równoległobok o
      bokach <InlineMath math={`a = ${aText}\\,\\text{cm}`} /> i{" "}
      <InlineMath math={`b = ${bText}\\,\\text{cm}`} />. Wysokość
      graniastosłupa wynosi{" "}
      <InlineMath math={`h = ${heightLatex}\\,\\text{cm}`} />. Oblicz pole
      powierzchni bocznej tego graniastosłupa.
    </p>
  );
}




if (
  variant === "regular_5" ||
  variant === "regular_6" ||
  variant === "regular_7"
) {
  const { aDisplay, hDisplay, nSides } = task;

  let prismAdj = "pięciokątny";
  if (nSides === 6) prismAdj = "sześciokątny";
  else if (nSides === 7) prismAdj = "siedmiokątny";

  return (
    <p>
      Dany jest graniastosłup prawidłowy {prismAdj} o boku{" "}
      <InlineMath math={`a = ${aDisplay}\\,\\text{cm}`} /> i wysokości{" "}
      <InlineMath math={`h = ${hDisplay}\\,\\text{cm}`} />. Oblicz pole
      powierzchni bocznej tego graniastosłupa.
    </p>
  );
}





  // fallback (nie powinien się zdarzyć)
  return (
    <p>
      Dany jest graniastosłup prosty. Oblicz pole powierzchni bocznej tego
      graniastosłupa.
    </p>
  );
}

// --- input (SqrtInput + "cm²" za oknem) ---

export function renderInput(taskOrWrapper, userAnswer, onChange, disabled) {
  unwrapTask(taskOrWrapper);

  return (
    <div className="flex items-center gap-2">
      <InlineMath math="P_b =" />
      <SqrtInput
        value={userAnswer || ""}
        onChange={onChange}
        disabled={disabled}
        label="P_b"
        suffixLatex="\text{cm}^2"
      />
    </div>
  );
}


// --- podgląd odpowiedzi ucznia ---

export function renderUserAnswer(userAnswer) {
  const raw =
    typeof userAnswer === "string"
      ? userAnswer
      : userAnswer?.raw ?? userAnswer?.parsed ?? "";

  if (!raw || String(raw).trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }

  const text = String(raw).trim();
  return <InlineMath math={`P_b = ${text}\\,\\text{cm}^2`} />;
}


// --- sprawdzanie odpowiedzi ---

export function checkAnswer(taskOrWrapper, userAnswer) {
  const task = unwrapTask(taskOrWrapper);
  if (!task) return false;

  const { perimeter, h, hIsRoot, hRootBase, hCoeff } = task;

  if (!userAnswer) return false;

  const raw =
    typeof userAnswer === "string"
      ? userAnswer
      : userAnswer.raw ?? userAnswer.parsed ?? "";

  if (!raw || String(raw).trim() === "") return false;

  const val = evaluateExpression(raw);
  if (typeof val !== "number" || !isFinite(val)) return false;

  // prawidłowa wartość pola bocznego
  const correct =
    hIsRoot && hRootBase
      ? perimeter * (hCoeff || 1) * Math.sqrt(hRootBase)
      : perimeter * h;

  // akceptujemy zarówno zapis z pierwiastkiem, jak i przybliżenie
  return Math.abs(val - correct) < 1e-2;
}

// Parser pierwiastka w latexie, np. "2\\sqrt{15}" albo "\\sqrt{15}"
function parseRoot(display) {
  if (!display || typeof display !== "string") return null;

  // postać k\sqrt{m}
  let m = display.match(/^(\d+)\\sqrt{(\d+)}$/);
  if (m) {
    return {
      coeff: parseInt(m[1], 10),
      base: parseInt(m[2], 10),
    };
  }

  // postać \sqrt{m}
  m = display.match(/^\\sqrt{(\d+)}$/);
  if (m) {
    return {
      coeff: 1,
      base: parseInt(m[1], 10),
    };
  }

  return null;
}

// Uproszczenie pierwiastka: np. sqrt(60) -> outside=2, inside=15
function simplifySqrt(n) {
  let outside = 1;
  let inside = n;

  for (let p = 2; p * p <= inside; p++) {
    const pp = p * p;
    while (inside % pp === 0) {
      outside *= p;
      inside /= pp;
    }
  }

  return { outside, inside };
}


// 2a * h zapisane w postaci k lub k√m (po uproszczeniu)
function build2ahTerm(sideValue, sideLatex, h, hIsRoot, hRootBase, hCoeff) {
  const rootSide = parseRoot(sideLatex);

  let sideCoeff, sideBase;
  if (rootSide) {
    sideCoeff = rootSide.coeff;   // np. 4 w 4√11
    sideBase = rootSide.base;     // np. 11
  } else {
    sideCoeff = sideValue;        // np. 2
    sideBase = 1;
  }

  let hCoeffLocal, hBase;
  if (hIsRoot && hRootBase != null && typeof hCoeff === "number") {
    hCoeffLocal = hCoeff;         // np. 2 w 2√3
    hBase = hRootBase;            // np. 3
  } else {
    hCoeffLocal = h;              // np. 5 (gdy brak pierwiastka)
    hBase = 1;
  }

  // 2 * side * h  →  (2 * sideCoeff * hCoeff) * √(sideBase * hBase)
  const combinedCoeff = 2 * sideCoeff * hCoeffLocal;
  const combinedBase = sideBase * hBase; // np. 11 * 3 = 33

  const { outside, inside } = simplifySqrt(combinedBase);
  const finalCoeff = combinedCoeff * outside;

  if (inside === 1) {
    // pierwiastek się całkowicie uprościł
    return `${finalCoeff}`;
  }
  return `${finalCoeff}\\sqrt{${inside}}`;
}


// --- poprawna odpowiedź ---

export function renderAnswer(taskOrWrapper) {
  const task = unwrapTask(taskOrWrapper);
  if (!task) return null;

  const {
    variant,
    nSides,
    a,
    b,
    c,
    aDisplay,
    bDisplay,
    cDisplay,
    perimeter,
    h,
    hDisplay,
    lateralArea,
    hIsRoot,
    hRootBase,
    hCoeff,
  } = task;

  const aLatex = aDisplay != null ? aDisplay : (a != null ? String(a) : "");
  const bLatex = bDisplay != null ? bDisplay : (b != null ? String(b) : "");
  const cLatex = cDisplay != null ? cDisplay : (c != null ? String(c) : "");
  const heightLatex =
    hDisplay != null ? hDisplay : (h != null ? String(h) : "");

  // --- SPECJALNY PRZYPADek: prostokąt / równoległobok ---
  if (variant === "rectangle" || variant === "parallelogram") {
    // obwód w najprostszej postaci (taki jak w Kroku 1)
    const rootA = parseRoot(aLatex);
    const rootB = parseRoot(bLatex);

    let termA;
    if (rootA) {
      const coeff = 2 * rootA.coeff;
      termA = `${coeff}\\sqrt{${rootA.base}}`;
    } else if (typeof a === "number") {
      termA = String(2 * a);
    }

    let termB;
    if (rootB) {
      const coeff = 2 * rootB.coeff;
      termB = `${coeff}\\sqrt{${rootB.base}}`;
    } else if (typeof b === "number") {
      termB = String(2 * b);
    }

    let opExact;
    if (termA && termB) {
      opExact = `${termA} + ${termB}`;     // np. 4 + 8√11
    } else if (termA) {
      opExact = termA;
    } else if (termB) {
      opExact = termB;
    } else {
      opExact = String(perimeter);
    }

    // teraz P_b = 2a·h + 2b·h, każdy człon uproszczony
  const termFromA = build2ahTerm(a, aLatex, h, hIsRoot, hRootBase, hCoeff);
  const termFromB = build2ahTerm(b, bLatex, h, hIsRoot, hRootBase, hCoeff);

  // sprawdzamy, czy gdziekolwiek pojawia się pierwiastek
  const hasRoot =
    (parseRoot(aLatex) != null) ||
    (parseRoot(bLatex) != null) ||
    (hIsRoot && hRootBase != null);

      let resultLatex;

  if (!hasRoot && typeof lateralArea === "number") {
    // czysto liczbowy przypadek → pokazujemy jedną liczbę (np. 198)
    resultLatex = String(lateralArea);
  } else if (termFromA && termFromB) {
    // próbujemy zsumować, jeśli oba składniki mają ten sam pierwiastek
    const rootA = parseRoot(termFromA);
    const rootB = parseRoot(termFromB);

    if (rootA && rootB && rootA.base === rootB.base) {
      // np. 32√10 + 40√10 → (32+40)√10 = 72√10
      const coeff = rootA.coeff + rootB.coeff;
      resultLatex = `${coeff}\\sqrt{${rootA.base}}`;
    } else {
      // różne pierwiastki → zostawiamy sumę
      resultLatex = `${termFromA} + ${termFromB}`;
    }
  } else if (termFromA) {
    resultLatex = termFromA;
  } else if (termFromB) {
    resultLatex = termFromB;
  } else {
    resultLatex = String(lateralArea);
  }


  const latex = `P_b = ${resultLatex}\\,\\text{cm}^2`;
  return <InlineMath math={latex} />;
  }




  const containsRoot = (s) =>
    typeof s === "string" && s.includes("\\sqrt");

  const sideHasRoot =
    containsRoot(aLatex) || containsRoot(bLatex) || containsRoot(cLatex);
  const heightHasRoot = containsRoot(heightLatex);

  // parser "3\\sqrt{5}" albo "\\sqrt{5}"
  function parseRoot(display) {
    if (!display || typeof display !== "string") return null;

    let m = display.match(/^(\d+)\\sqrt{(\d+)}$/);
    if (m) {
      return { coeff: parseInt(m[1], 10), base: parseInt(m[2], 10) };
    }

    m = display.match(/^\\sqrt{(\d+)}$/);
    if (m) {
      return { coeff: 1, base: parseInt(m[1], 10) };
    }

    return null;
  }

    // rozkład sqrt(n) = outside * sqrt(inside), np. sqrt(18) -> 3*sqrt(2)
  function simplifySqrt(n) {
    let outside = 1;
    let inside = n;

    for (let p = 2; p * p <= inside; p++) {
      const pp = p * p;
      while (inside % pp === 0) {
        outside *= p;
        inside /= pp;
      }
    }

    return { outside, inside };
  }


  // symboliczny O_p
  let opLatex;
  if (variant === "rectangle" || variant === "parallelogram") {
  const rootA = parseRoot(aLatex);
  const rootB = parseRoot(bLatex);

  let termA;
  if (rootA) {
    const coeff = 2 * rootA.coeff;
    termA = `${coeff}\\sqrt{${rootA.base}}`;
  } else if (typeof a === "number") {
    termA = String(2 * a);
  }

  let termB;
  if (rootB) {
    const coeff = 2 * rootB.coeff;
    termB = `${coeff}\\sqrt{${rootB.base}}`;
  } else if (typeof b === "number") {
    termB = String(2 * b);
  }

  if (termA && termB) {
    opLatex = `${termA} + ${termB}`;          // np. 4√15 + 12
  } else if (termA) {
    opLatex = termA;
  } else if (termB) {
    opLatex = termB;
  } else {
    opLatex = String(perimeter);
  }
}
  if (variant === "triangle_equilateral") {
    opLatex = `3\\cdot ${aLatex}`;
  } else if (variant === "square") {
    opLatex = `4\\cdot ${aLatex}`;
  } else if (variant === "rhombus") {
    opLatex = `4\\cdot ${aLatex}`;
  } else if (
    variant === "regular_5" ||
    variant === "regular_6" ||
    variant === "regular_7"
  ) {
    const sidesCount =
      nSides ||
      (variant === "regular_5"
        ? 5
        : variant === "regular_6"
        ? 6
        : 7);
    opLatex = `${sidesCount}\\cdot ${aLatex}`;
  } else if (variant === "rectangle" || variant === "parallelogram") {
    opLatex = `2\\cdot ${aLatex} + 2\\cdot ${bLatex}`;
  } else if (variant === "triangle_scalene" || variant === "triangle_right") {
    opLatex = `${aLatex} + ${bLatex} + ${cLatex}`;
  } else {
    opLatex = String(perimeter);
  }

  let latex;

  // 1) Brak pierwiastków w ogóle → zwykła liczba (np. 160 cm^2)
  if (!sideHasRoot && !heightHasRoot) {
    const areaValue =
      typeof lateralArea === "number"
        ? lateralArea
        : perimeter * h;

    latex = `P_b = ${areaValue}\\,\\text{cm}^2`;
  }

  // 2) Pierwiastek tylko w wysokości (boki całkowite) → k√m cm^2
  else if (
    heightHasRoot &&
    !sideHasRoot &&
    hIsRoot &&
    hRootBase != null &&
    typeof perimeter === "number" &&
    typeof hCoeff === "number"
  ) {
    const coeffTotal = perimeter * (hCoeff || 1);
    latex = `P_b = ${coeffTotal}\\sqrt{${hRootBase}}\\,\\text{cm}^2`;
  }

  // 3) Pierwiastek tylko w jednym boku, wysokość całkowita
  //    i obwód zależy od jednego boku: 3a / 4a / n·a
  else if (
    !heightHasRoot &&
    sideHasRoot &&
    (variant === "triangle_equilateral" ||
      variant === "square" ||
      variant === "rhombus" ||
      variant === "regular_5" ||
      variant === "regular_6" ||
      variant === "regular_7") &&
    typeof h === "number"
  ) {
    const rootInfo = parseRoot(aLatex);
    if (rootInfo) {
      let mult;
      if (variant === "triangle_equilateral") {
        mult = 3;
      } else if (variant === "square" || variant === "rhombus") {
        mult = 4;
      } else {
        const sidesCount =
          nSides ||
          (variant === "regular_5"
            ? 5
            : variant === "regular_6"
            ? 6
            : 7);
        mult = sidesCount;
      }

      const coeffTotal = mult * rootInfo.coeff * h;
      const base = rootInfo.base;
      latex = `P_b = ${coeffTotal}\\sqrt{${base}}\\,\\text{cm}^2`;
    } else {
      latex = `P_b = ${opLatex}\\cdot ${heightLatex}\\,\\text{cm}^2`;
    }
  }

  // 4) Pierwiastek w boku i w wysokości – JEDEN bok w obwodzie (3a / 4a / n·a)
  //    → mnożymy do końca, ale nadal dokładnie
  else if (
    sideHasRoot &&
    heightHasRoot &&
    (variant === "triangle_equilateral" ||
      variant === "square" ||
      variant === "rhombus" ||
      variant === "regular_5" ||
      variant === "regular_6" ||
      variant === "regular_7")
  ) {
    const sideRoot = parseRoot(aLatex);
    const heightRoot =
      (hIsRoot && hRootBase != null && typeof hCoeff === "number"
        ? { coeff: hCoeff, base: hRootBase }
        : parseRoot(heightLatex));

    if (sideRoot && heightRoot) {
      let mult;
      if (variant === "triangle_equilateral") {
        mult = 3;
      } else if (variant === "square" || variant === "rhombus") {
        mult = 4;
      } else {
        const sidesCount =
          nSides ||
          (variant === "regular_5"
            ? 5
            : variant === "regular_6"
            ? 6
            : 7);
        mult = sidesCount;
      }

      const coeffTotal = mult * sideRoot.coeff * heightRoot.coeff;
      const base1 = sideRoot.base;
      const base2 = heightRoot.base;

      const combined = base1 * base2;

       // rozkładamy sqrt(combined) = outside * sqrt(inside)
       const { outside, inside } = simplifySqrt(combined);
       const resultCoeff = coeffTotal * outside;

      if (inside === 1) {
         // pierwiastek całkowicie zniknął
         latex = `P_b = ${resultCoeff}\\,\\text{cm}^2`;
       } else {
         // wyciągnęliśmy część całkowitą, np. 180√18 -> 540√2
         latex = `P_b = ${resultCoeff}\\sqrt{${inside}}\\,\\text{cm}^2`;
       }
    }
  }

  // 5) Wszystkie inne, bardziej skomplikowane kombinacje
  //    → pokazujemy dokładny iloczyn O_p · h, bez przybliżeń
  else {
  latex = `P_b = \\left(${opLatex}\\right)\\cdot ${heightLatex}\\,\\text{cm}^2`;
}


  return (
    <span>
      <InlineMath math={latex} />
    </span>
  );
}



// --- wyjaśnienie krok po kroku ---

export function renderExplanation(taskOrWrapper) {
  const task = unwrapTask(taskOrWrapper);
  if (!task) return null;

  const {
  variant,
  nSides,
  a,
  b,
  c,
  h,
  perimeter,
  lateralArea,
  givenType,
  hDisplay,
  hIsRoot,
  hRootBase,
  hCoeff,
  aDisplay,
  bDisplay,
  cDisplay,
} = task;




const heightLatex = hDisplay || h;

const aLatex = aDisplay != null ? aDisplay : (a != null ? String(a) : "");
const bLatex = bDisplay != null ? bDisplay : (b != null ? String(b) : "");
const cLatex = cDisplay != null ? cDisplay : (c != null ? String(c) : "");

  // symboliczny O_p – kopiujemy z renderAnswer
  let opLatex;
  if (variant === "triangle_equilateral") {
    opLatex = `3\\cdot ${aLatex}`;
  } else if (variant === "square") {
    opLatex = `4\\cdot ${aLatex}`;
  } else if (variant === "rhombus") {
    opLatex = `4\\cdot ${aLatex}`;
  } else if (
    variant === "regular_5" ||
    variant === "regular_6" ||
    variant === "regular_7"
  ) {
    const sidesCount =
      nSides ||
      (variant === "regular_5"
        ? 5
        : variant === "regular_6"
        ? 6
        : 7);
    opLatex = `${sidesCount}\\cdot ${aLatex}`;
  } else if (variant === "rectangle" || variant === "parallelogram") {
    opLatex = `2\\cdot ${aLatex} + 2\\cdot ${bLatex}`;
  } else if (variant === "triangle_scalene" || variant === "triangle_right") {
    opLatex = `${aLatex} + ${bLatex} + ${cLatex}`;
  } else {
    opLatex = String(perimeter);
  }


const containsRoot = (s) =>
  typeof s === "string" && s.includes("\\sqrt");

let pythagorasMath = null;
  const parseRootForOp = parseRoot;


let perimeterMath;
  // ==========================================
  //  Zmienna: opSubLatex – to, co w Kroku 1
  //  jest ostatnią postacią O_p (do podstawienia
  //  w Kroku 2)
  // ==========================================
  let opSubLatex = null;



  if (variant === "triangle_right") {
    // Krok z Pitagorasem – w zależności od tego, które boki były dane
    if (givenType === "legs") {
      const a2 = a * a;
      const b2 = b * b;
      const sum = a2 + b2;
      pythagorasMath =
        "\\begin{aligned}\n" +
        "c^2 &= a^2 + b^2\\\\\n" +
        `c^2 &= ${a}^2 + ${b}^2\\\\\n` +
        `c^2 &= ${a2} + ${b2}\\\\\n` +
        `c^2 &= ${sum}\\\\\n` +
        `c &= \\sqrt{${sum}}\n` +
        "\\end{aligned}";
    } else if (givenType === "a_c") {
      const c2 = c * c;
      const a2 = a * a;
      const diff = c2 - a2;
      pythagorasMath =
        "\\begin{aligned}\n" +
        "c^2 &= a^2 + b^2\\\\\n" +
        `b^2 &= c^2 - a^2\\\\\n` +
        `b^2 &= ${c}^2 - ${a}^2\\\\\n` +
        `b^2 &= ${c2} - ${a2}\\\\\n` +
        `b^2 &= ${diff}\\\\\n` +
        `b &= \\sqrt{${diff}}\n` +
        "\\end{aligned}";
    } else if (givenType === "b_c") {
      const c2 = c * c;
      const b2 = b * b;
      const diff = c2 - b2;
      pythagorasMath =
        "\\begin{aligned}\n" +
        "c^2 &= a^2 + b^2\\\\\n" +
        `a^2 &= c^2 - b^2\\\\\n` +
        `a^2 &= ${c}^2 - ${b}^2\\\\\n` +
        `a^2 &= ${c2} - ${b2}\\\\\n` +
        `a^2 &= ${diff}\\\\\n` +
        `a &= \\sqrt{${diff}}\n` +
        "\\end{aligned}";
    }
  }

   if (variant === "triangle_scalene") {
    const hasRootHere =
      containsRoot(aLatex) || containsRoot(bLatex) || containsRoot(cLatex);

    const lastLine = hasRootHere
      ? `&= ${aLatex} + ${bLatex} + ${cLatex}\\,\\text{cm}\n`
      : `&= ${perimeter}\\,\\text{cm}\n`;

    perimeterMath =
      "\\begin{aligned}\n" +
      "O_p &= a + b + c\\\\\n" +
      `&= ${aLatex} + ${bLatex} + ${cLatex}\\\\\n` +
      lastLine +
      "\\end{aligned}";
  } else if (variant === "triangle_right") {
    const hasRootHere =
      containsRoot(aLatex) || containsRoot(bLatex) || containsRoot(cLatex);

    const lastLine = hasRootHere
      ? `&= ${aLatex} + ${bLatex} + ${cLatex}\\,\\text{cm}\n`
      : `&= ${perimeter}\\,\\text{cm}\n`;

    perimeterMath =
      "\\begin{aligned}\n" +
      "O_p &= a + b + c\\\\\n" +
      `&= ${aLatex} + ${bLatex} + ${cLatex}\\\\\n` +
      lastLine +
      "\\end{aligned}";
  } else if (variant === "triangle_equilateral") {
    // bok a – być może w postaci k\sqrt{m}
    const root = parseRootForOp(aLatex);

    let lastLine;
    if (root) {
      const totalCoeff = 3 * root.coeff;      // 3a → 3k√m = (3k)√m
      lastLine = `&= ${totalCoeff}\\sqrt{${root.base}}\\,\\text{cm}\n`;
    } else {
      lastLine = `&= ${perimeter}\\,\\text{cm}\n`;
    }

    perimeterMath =
      "\\begin{aligned}\n" +
      "O_p &= 3a\\\\\n" +
      `&= 3\\cdot ${aLatex}\\\\\n` +
      lastLine +
      "\\end{aligned}";
  } else if (variant === "square") {
    const root = parseRootForOp(aLatex);

    let lastLine;
    if (root) {
      const totalCoeff = 4 * root.coeff;      // 4a → 4k√m = (4k)√m
      lastLine = `&= ${totalCoeff}\\sqrt{${root.base}}\\,\\text{cm}\n`;
    } else {
      lastLine = `&= ${perimeter}\\,\\text{cm}\n`;
    }

    perimeterMath =
      "\\begin{aligned}\n" +
      "O_p &= 4a\\\\\n" +
      `&= 4\\cdot ${aLatex}\\\\\n` +
      lastLine +
      "\\end{aligned}";
  } else if (variant === "rectangle" || variant === "parallelogram") {
  const rootA = parseRootForOp(aLatex);
  const rootB = parseRootForOp(bLatex);

  // 2a i 2b po uproszczeniu
  let termA;
  if (rootA) {
    const coeff = 2 * rootA.coeff; // np. 2 * 4 = 8
    termA = `${coeff}\\sqrt{${rootA.base}}`;
  } else if (typeof a === "number") {
    termA = String(2 * a);
  }

  let termB;
  if (rootB) {
    const coeff = 2 * rootB.coeff;
    termB = `${coeff}\\sqrt{${rootB.base}}`;
  } else if (typeof b === "number") {
    termB = String(2 * b);
  }

  let lastLine;
  if (termA && termB) {
    lastLine = `&= ${termA} + ${termB}\\,\\text{cm}\n`;   // np. 4 + 8√11
  } else if (termA) {
    lastLine = `&= ${termA}\\,\\text{cm}\n`;
  } else if (termB) {
    lastLine = `&= ${termB}\\,\\text{cm}\n`;
  } else {
    lastLine = `&= ${perimeter}\\,\\text{cm}\n`;
  }

  // zapamiętujemy dokładny obwód (bez jednostki) dla Kroku 2
  opSubLatex =
    lastLine
      .replace(/^&= /, "")
      .replace(/\\,\\text\{cm\}\n?$/, "");

  const expr = `2\\cdot ${aLatex} + 2\\cdot ${bLatex}`;

  perimeterMath =
    "\\begin{aligned}\n" +
    "O_p &= 2a + 2b\\\\\n" +
    `&= ${expr}\\\\\n` +
    lastLine +
    "\\end{aligned}";
  } else if (variant === "rhombus") {
    const root = parseRootForOp(aLatex);

    let lastLine;
    if (root) {
      const totalCoeff = 4 * root.coeff;      // 4a → (4k)√m
      lastLine = `&= ${totalCoeff}\\sqrt{${root.base}}\\,\\text{cm}\n`;
    } else {
      lastLine = `&= ${perimeter}\\,\\text{cm}\n`;
    }

    perimeterMath =
      "\\begin{aligned}\n" +
      "O_p &= 4a\\\\\n" +
      `&= 4\\cdot ${aLatex}\\\\\n` +
      lastLine +
      "\\end{aligned}";
  } else if (variant === "parallelogram") {
    const hasRootHere =
      containsRoot(aLatex) || containsRoot(bLatex);
    const expr = `2\\cdot ${aLatex} + 2\\cdot ${bLatex}`;

    const lastLine = hasRootHere
      ? `&= ${expr}\\,\\text{cm}\n`
      : `&= ${perimeter}\\,\\text{cm}\n`;

    perimeterMath =
      "\\begin{aligned}\n" +
      "O_p &= 2a + 2b\\\\\n" +
      `&= ${expr}\\\\\n` +
      lastLine +
      "\\end{aligned}";
  } else if (
    variant === "regular_5" ||
    variant === "regular_6" ||
    variant === "regular_7"
  ) {
    const n = nSides;
    const root = parseRootForOp(aLatex);

    let lastLine;
    if (root) {
      const totalCoeff = n * root.coeff;      // n*a → (n*k)√m
      lastLine = `&= ${totalCoeff}\\sqrt{${root.base}}\\,\\text{cm}\n`;
    } else {
      lastLine = `&= ${perimeter}\\,\\text{cm}\n`;
    }

    perimeterMath =
      "\\begin{aligned}\n" +
      "O_p &= n\\cdot a\\\\\n" +
      `&= ${n}\\cdot ${aLatex}\\\\\n` +
      lastLine +
      "\\end{aligned}";
  } else {
    // awaryjnie
    perimeterMath =
      "\\begin{aligned}\n" +
      `O_p &= ${perimeter}\\,\\text{cm}\n` +
      "\\end{aligned}";
  }




     let surfaceMath;

// używamy globalnych helperów
const parseRootForSurface = parseRoot;
const simplifySqrtForSurface = simplifySqrt;

  const sideRoot = parseRootForSurface(aDisplay);
  const heightRoot =
    hIsRoot && hRootBase != null && typeof hCoeff === "number"
      ? { coeff: hCoeff, base: hRootBase }
      : parseRootForSurface(hDisplay);

  const simpleNAvariants = [
    "triangle_equilateral",
    "square",
    "rhombus",
    "regular_5",
    "regular_6",
    "regular_7",
  ];

  let usedExact = false;

  // Przypadek 1: pierwiastek tylko w wysokości, boki bez pierwiastka
  // O_p jest liczbą (perimeter), h = k√m → P_b = perimeter * k√m
  if (
    !usedExact &&
    !sideRoot && // w bokach brak pierwiastka
    heightRoot && // pierwiastek w wysokości
    typeof perimeter === "number"
  ) {
    const hCoeffLocal = heightRoot.coeff; // np. 2 w 2√11
    const base = heightRoot.base; // np. 11

    const coeffTotal = perimeter * hCoeffLocal; // np. 35 * 2 = 70
    const opSubLatex = String(perimeter); // O_p obliczone wyżej

    surfaceMath =
      "\\begin{aligned}\n" +
      "P_b &= O_p \\cdot h\\\\\n" +
      `&= ${opSubLatex}\\cdot ${heightLatex}\\\\\n` + // 35 · 2√11
      `&= ${coeffTotal}\\sqrt{${base}}\\,\\text{cm}^2\n` + // 70√11
      "\\end{aligned}";

    usedExact = true;
  }

  // Przypadek 2: pierwiastek tylko w boku, wysokość całkowita
  // Podstawa foremna → O_p = n * a, a = k√m → O_p = (n*k)√m
  if (
    !usedExact &&
    sideRoot && // bok ma pierwiastek
    !heightRoot && // wysokość bez pierwiastka
    simpleNAvariants.includes(variant) &&
    typeof h === "number"
  ) {
    let nVertices;
    if (variant === "triangle_equilateral") {
      nVertices = 3;
    } else if (variant === "square" || variant === "rhombus") {
      nVertices = 4;
    } else {
      // regular_5 / _6 / _7
      nVertices = nSides;
    }

    const base = sideRoot.base; // np. 6
    const coeffSide = sideRoot.coeff; // np. 5

    // O_p = (n * coeffSide)√base, np. 25√6
    const opCoeff = nVertices * coeffSide;
    const opExactLatex = `${opCoeff}\\sqrt{${base}}`;

    // P_b = (opCoeff√base) * h → (opCoeff*h)√base
    const areaCoeff = opCoeff * h; // np. 25*2 = 50

    surfaceMath =
      "\\begin{aligned}\n" +
      "P_b &= O_p \\cdot h\\\\\n" +
      `&= ${opExactLatex}\\cdot ${h}\\\\\n` + // 25√6 · 2
      `&= ${areaCoeff}\\sqrt{${base}}\\,\\text{cm}^2\n` + // 50√6
      "\\end{aligned}";

    usedExact = true;
  }

  // Przypadek 3: obwód = n * a, a = k√m, h = l√m (ta sama podstawa pierwiastka)
  // Podstawa foremna, ten sam pierwiastek w boku i w wysokości
  if (
    !usedExact &&
    sideRoot &&
    heightRoot &&
    sideRoot.base === heightRoot.base &&
    simpleNAvariants.includes(variant)
  ) {
    const base = sideRoot.base;

    let nVertices;
    if (variant === "triangle_equilateral") {
      nVertices = 3;
    } else if (variant === "square" || variant === "rhombus") {
      nVertices = 4;
    } else {
      // regular_5 / regular_6 / regular_7
      nVertices = nSides;
    }

    // O_p = n * a = n * (k√m) = (n*k)√m
    const opCoeff = nVertices * sideRoot.coeff;
    const hCoeffLocal = heightRoot.coeff;

    // P_b = (opCoeff√m) * (hCoeff√m) = opCoeff*hCoeff*m
    const areaCoeff = opCoeff * hCoeffLocal * base;

    const opExactLatex = `${opCoeff}\\sqrt{${base}}`;
    const hExactLatex =
      hDisplay != null ? hDisplay : `${hCoeffLocal}\\sqrt{${base}}`;

    surfaceMath =
      "\\begin{aligned}\n" +
      "P_b &= O_p \\cdot h\\\\\n" +
      `&= ${opExactLatex}\\cdot ${hExactLatex}\\\\\n` +
      `&= ${areaCoeff}\\,\\text{cm}^2\n` +
      "\\end{aligned}";

    usedExact = true;
  }

  // Przypadek 4: pierwiastek w boku i w wysokości, różne podstawy pierwiastka
  // Podstawa foremna → najpierw iloczyn pierwiastków, potem wyciągamy część całkowitą
    // wspólny przypadek: podstawa foremna → obwód w postaci n·a
  if (
    !usedExact &&
    sideRoot &&
    heightRoot &&
    simpleNAvariants.includes(variant)
  ) {
    let nVertices;
    if (variant === "triangle_equilateral") {
      nVertices = 3;
    } else if (variant === "square" || variant === "rhombus") {
      nVertices = 4;
    } else {
      // regular_5 / _6 / _7
      nVertices = nSides;
    }

    const aCoeff = sideRoot.coeff;        // np. 5 w 5√6
    const aBase = sideRoot.base;          // np. 6
    const hCoeffLocal = heightRoot.coeff; // np. 3
    const hBase = heightRoot.base;        // np. 3

    // O_p = n * a = (n * aCoeff)√aBase
    const opCoeff = nVertices * aCoeff;              // np. 5*5 = 25
    const opExactLatex = `${opCoeff}\\sqrt{${aBase}}`; // 25√6

    // P_b = (opCoeff√aBase) * (hCoeff√hBase)
    //     = (opCoeff * hCoeff)√(aBase * hBase)
    const areaCoeff = opCoeff * hCoeffLocal;         // np. 25*3 = 75
    const combinedBase = aBase * hBase;              // np. 6*3 = 18

    // próbujemy uprościć pierwiastek: sqrt(combinedBase) = outside * sqrt(inside)
    const { outside, inside } = simplifySqrt(combinedBase);
    const simplifiedCoeff = areaCoeff * outside;

    let body;

    if (inside === 1) {
      // pierwiastek całkowicie zniknął
      body =
        `&= ${opExactLatex}\\cdot ${heightLatex}\\\\\n` +       // 25√6 · 3√3
        `&= ${areaCoeff}\\sqrt{${combinedBase}}\\\\\n` +        // 75√18
        `&= ${simplifiedCoeff}\\,\\text{cm}^2\n`;               // 225 cm^2
    } else if (inside !== combinedBase) {
      // pierwiastek się uprościł, np. 75√18 -> 75·3√2 = 225√2
      body =
        `&= ${opExactLatex}\\cdot ${heightLatex}\\\\\n` +       // 25√6 · 3√3
        `&= ${areaCoeff}\\sqrt{${combinedBase}}\\\\\n` +        // 75√18
        `&= ${simplifiedCoeff}\\sqrt{${inside}}\\,\\text{cm}^2\n`; // 225√2
    } else {
      // nic się nie uprościło – zostajemy przy areaCoeff√combinedBase
      body =
        `&= ${opExactLatex}\\cdot ${heightLatex}\\\\\n` +
        `&= ${areaCoeff}\\sqrt{${combinedBase}}\\,\\text{cm}^2\n`;
    }

    surfaceMath =
      "\\begin{aligned}\n" +
      "P_b &= O_p \\cdot h\\\\\n" +
      body +
      "\\end{aligned}";

    usedExact = true;
  }


    // Specjalny przypadek: prostokąt / równoległobok – rozbijamy (2a + 2b)·h
  if (
    !usedExact &&
    (variant === "rectangle" || variant === "parallelogram")
  ) {
    // jeśli z Kroku 1 mamy już obliczony obwód, używamy go
    const opForSub = opSubLatex || opLatex;

    const termFromA = build2ahTerm(a, aLatex, h, hIsRoot, hRootBase, hCoeff);
    const termFromB = build2ahTerm(b, bLatex, h, hIsRoot, hRootBase, hCoeff);

    const hasRootHere =
      containsRoot(aLatex) ||
      containsRoot(bLatex) ||
      containsRoot(heightLatex);

    if (!hasRootHere && typeof lateralArea === "number") {
      // czysto liczbowy przypadek (np. a=7, b=4, h=9)
      const sumParts = [];
      if (termFromA) sumParts.push(termFromA);
      if (termFromB) sumParts.push(termFromB);
      const sumLine = sumParts.join(" + ");

      surfaceMath =
        "\\begin{aligned}\n" +
        "P_b &= O_p \\cdot h\\\\\n" +
        `&= \\left(${opForSub}\\right)\\cdot ${heightLatex}\\\\\n` +
        `&= ${sumLine}\\\\\n` +
        `&= ${lateralArea}\\,\\text{cm}^2\n` +
        "\\end{aligned}";
    } else {
      // mamy pierwiastki – suma uproszczonych członów 2a·h i 2b·h
      let resultLatex;
      if (termFromA && termFromB) {
        resultLatex = `${termFromA} + ${termFromB}`;
      } else if (termFromA) {
        resultLatex = termFromA;
      } else if (termFromB) {
        resultLatex = termFromB;
      } else {
        resultLatex = String(lateralArea);
      }

      surfaceMath =
        "\\begin{aligned}\n" +
        "P_b &= O_p \\cdot h\\\\\n" +
        `&= \\left(${opForSub}\\right)\\cdot ${heightLatex}\\\\\n` +
        `&= ${resultLatex}\\,\\text{cm}^2\n` +
        "\\end{aligned}";
    }

    // prostokąt / równoległobok są już obsłużone, fallback ma się nie odpalać
    usedExact = true;
  }




  // jeśli nie weszliśmy w żaden dokładny przypadek, wersja ogólna z "obliczonym" O_p
  if (
  !usedExact &&
  variant !== "rectangle" &&
  variant !== "parallelogram"
) {
  const hasRootInSides =
    containsRoot(aLatex) || containsRoot(bLatex) || containsRoot(cLatex);
  const hasRootInHeight = containsRoot(heightLatex);
  const hasRootAnywhere = hasRootInSides || hasRootInHeight;

  // 1) jeśli mamy przygotowane opSubLatex z Kroku 1 (np. "4√15 + 12"),
  //    to używamy właśnie tego
  let opForSub = opSubLatex;

  // 2) w przeciwnym razie:
  //    - jeśli w bokach nie ma pierwiastków, bierzemy samą liczbę perimeter,
  //    - w przeciwnym razie zostawiamy symboliczne opLatex (np. "2·a + 2·b")
  if (!opForSub) {
    opForSub =
      !hasRootInSides && typeof perimeter === "number"
        ? String(perimeter)
        : opLatex;
  }

  if (hasRootAnywhere) {
    // są pierwiastki → ZERO przybliżeń, pokazujemy tylko iloczyn
    surfaceMath =
      "\\begin{aligned}\n" +
      "P_b &= O_p \\cdot h\\\\\n" +
      `&= \\left(${opForSub}\\right)\\cdot ${heightLatex}\\,\\text{cm}^2\n` +
      "\\end{aligned}";
  } else {
    // brak pierwiastków → można spokojnie policzyć dokładną liczbę
    surfaceMath =
      "\\begin{aligned}\n" +
      "P_b &= O_p \\cdot h\\\\\n" +
      `&= ${opForSub}\\cdot ${heightLatex}\\\\\n` +
      `&= ${lateralArea}\\,\\text{cm}^2\n` +
      "\\end{aligned}";
  }
}


  // osobna wersja dla trójkąta prostokątnego – 3 kroki
  if (variant === "triangle_right" && pythagorasMath) {
    return (
      <div className="space-y-4" style={{ textAlign: "left" }}>
        <div>
          <strong>Krok 1:</strong>{" "}
          Obliczamy brakującą długość boku trójkąta z twierdzenia Pitagorasa.
          <BlockMath math={pythagorasMath} />
        </div>

        <div>
          <strong>Krok 2:</strong> Obliczamy obwód podstawy.
          <BlockMath math={perimeterMath} />
        </div>

        <div>
          <strong>Krok 3:</strong> Korzystamy ze wzoru na pole powierzchni
          bocznej graniastosłupa i podstawiamy obliczone wartości.
          <BlockMath
            math={
              "\\begin{aligned}\n" +
              "P_b &= O_p \\cdot h\n" +
              "\\end{aligned}"
            }
          />
          <BlockMath math={surfaceMath} />
        </div>
      </div>
    );
  }

  // wersja ogólna (bez Pitagorasa)
  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1:</strong> Obliczamy obwód podstawy.
        <BlockMath math={perimeterMath} />
      </div>

      <div>
        <strong>Krok 2:</strong> Korzystamy ze wzoru na pole powierzchni bocznej
        graniastosłupa:
        <BlockMath
          math={
            "\\begin{aligned}\n" +
            "P_b &= O_p \\cdot h\n" +
            "\\end{aligned}"
          }
        />
        oraz podstawiamy obliczone wartości:
        <BlockMath math={surfaceMath} />
      </div>
    </div>
  );
}
